import Vue from 'vue';
import _ from 'lodash';
import { defineStore } from 'pinia';

import TableReservationApi from '@/extensions/tableReservation/store/api/tableReservation';
import {
    buildCombinationInput,
    buildFixtureInput,
    buildRoomInput,
    buildTableInput,
    getDefaultSeatSides,
    getFixtureDefaultSize,
    normalizeCombination,
    normalizeFixture,
    normalizeRoom,
    normalizeTable
} from '@/extensions/tableReservation/store/utils';
import { useSetupStore } from '@/stores/setup';

const emptyRoom = {
    id: null,
    name: '',
    width: 600,
    height: 400,
    position: 0
};

function sortRooms(rooms) {
    return _.sortBy((rooms || []).map(normalizeRoom), ['position', 'name']);
}

function sortTables(tables) {
    return _.sortBy((tables || []).map(normalizeTable), [
        (table) => {
            return table.roomId;
        },
        (table) => {
            return parseInt(table.number, 10) || 0;
        },
        'name'
    ]);
}

function sortFixtures(fixtures) {
    return _.sortBy((fixtures || []).map(normalizeFixture), [
        (fixture) => {
            return fixture.roomId;
        },
        'position',
        'id'
    ]);
}

function defaultFixtureLabel(type) {
    if (type === 'rectangle') {
        return 'Tresen';
    }

    if (type === 'stairs' || type === 'spiral-stairs') {
        return 'Treppe';
    }

    if (type === 'area-link') {
        return 'Bereich';
    }

    return '';
}

function sortCombinations(combinations) {
    return _.sortBy((combinations || []).map(normalizeCombination), ['position', 'name']);
}

function getCmsId() {
    const cmsId = _.get(useSetupStore(), 'activeCms.id');
    if (!cmsId) {
        throw new Error('No active CMS selected');
    }

    return parseInt(cmsId, 10);
}

function normalizeTableIds(tableIds) {
    const seen = {};

    return (tableIds || []).map((tableId) => {
        if (tableId === undefined || tableId === null || tableId === '') {
            return null;
        }

        return String(tableId);
    }).filter((tableId) => {
        if (!tableId || seen[tableId]) {
            return false;
        }

        seen[tableId] = true;
        return true;
    });
}

function sortTableIdsByTableOrder(tableIds, tables) {
    const tableIndexById = {};
    (tables || []).forEach((table, index) => {
        tableIndexById[String(table.id)] = index;
    });

    return normalizeTableIds(tableIds).slice().sort((left, right) => {
        const leftIndex = Object.prototype.hasOwnProperty.call(tableIndexById, left) ? tableIndexById[left] : Infinity;
        const rightIndex = Object.prototype.hasOwnProperty.call(tableIndexById, right) ? tableIndexById[right] : Infinity;

        if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
        }

        return left.localeCompare(right);
    });
}

function buildCombinationSignature(tableIds) {
    return normalizeTableIds(tableIds).slice().sort((left, right) => {
        const leftNumber = parseInt(left, 10);
        const rightNumber = parseInt(right, 10);
        const leftIsNumber = !Number.isNaN(leftNumber);
        const rightIsNumber = !Number.isNaN(rightNumber);

        if (leftIsNumber && rightIsNumber && leftNumber !== rightNumber) {
            return leftNumber - rightNumber;
        }

        return left.localeCompare(right);
    }).join(':');
}

export const useTableReservationFloorPlanStore = defineStore('tableReservationFloorPlan', {
    state: function () {
        return {
            loading: false,
            rooms: [],
            tables: [],
            fixtures: [],
            selectedFixtureId: null,
            combinations: [],
            selectedTableId: null,
            selectedTableIds: [],
            selectedRoomId: null,
            editMode: false
        };
    },

    getters: {
        currentRoom: function (state) {
            return state.rooms.find((room) => {
                return room.id === state.selectedRoomId;
            }) || state.rooms[0] || _.cloneDeep(emptyRoom);
        },
        currentRoomTables: function (state) {
            return state.tables.filter((table) => {
                return table.roomId === state.selectedRoomId;
            });
        },
        selectedTable: function (state) {
            if (!state.selectedTableId) {
                return null;
            }

            return state.tables.find((table) => {
                return table.id === state.selectedTableId;
            }) || null;
        },
        currentRoomFixtures: function (state) {
            return state.fixtures.filter((fixture) => {
                return fixture.roomId === state.selectedRoomId;
            });
        },
        selectedFixture: function (state) {
            if (!state.selectedFixtureId) {
                return null;
            }

            return state.fixtures.find((fixture) => {
                return fixture.id === state.selectedFixtureId;
            }) || null;
        },
        tablesByRoom: function (state) {
            return function (roomId) {
                return state.tables.filter((table) => {
                    return table.roomId === roomId;
                });
            };
        },
        allTableOptions: function (state) {
            return state.tables.map((table) => {
                const room = state.rooms.find((candidate) => {
                    return candidate.id === table.roomId;
                });

                return {
                    id: table.id,
                    label: table.name + ' (' + (room ? room.name : '') + ', ' + table.seats + ' Plätze)',
                    seats: table.seats,
                    roomId: table.roomId,
                    roomName: room ? room.name : ''
                };
            });
        },
        combinationsForTable: function (state) {
            return function (tableId) {
                const id = String(tableId);
                return state.combinations.filter((combination) => {
                    return (combination.tableIds || []).indexOf(id) !== -1;
                });
            };
        }
    },

    actions: {
        hydrateBoard: function (payload) {
            const currentRoomId = this.selectedRoomId;
            const currentTableId = this.selectedTableId;

            this.rooms = sortRooms(_.get(payload, 'rooms', []));
            this.tables = sortTables(_.get(payload, 'tables', []));
            this.fixtures = sortFixtures(_.get(payload, 'fixtures', []));
            this.combinations = sortCombinations(_.get(payload, 'combinations', []));

            if (currentRoomId && this.rooms.find((room) => { return room.id === currentRoomId; })) {
                this.selectedRoomId = currentRoomId;
            } else {
                this.selectedRoomId = _.get(this.rooms, '0.id', null);
            }

            if (currentTableId && this.tables.find((table) => { return table.id === currentTableId; })) {
                this.selectedTableId = currentTableId;
            } else {
                this.selectedTableId = null;
            }

            const existingTableIds = {};
            this.tables.forEach((table) => { existingTableIds[table.id] = true; });
            this.selectedTableIds = (this.selectedTableIds || []).filter((id) => {
                return existingTableIds[id];
            });

            if (!this.fixtures.find((fixture) => { return fixture.id === this.selectedFixtureId; })) {
                this.selectedFixtureId = null;
            }
        },

        setSelectedRoom: function (roomId) {
            this.selectedRoomId = roomId;
            this.selectedTableId = null;
            this.selectedFixtureId = null;
        },

        selectTable: function (tableId) {
            this.selectedTableId = tableId;
            this.selectedFixtureId = null;
        },

        selectFixture: function (fixtureId) {
            this.selectedFixtureId = fixtureId;
            this.selectedTableId = null;
            this.selectedTableIds = [];
        },

        deselectFixture: function () {
            this.selectedFixtureId = null;
        },

        deselectTable: function () {
            this.selectedTableId = null;
        },

        toggleTableSelection: function (tableId, additive) {
            const id = String(tableId);
            if (!additive) {
                this.selectedTableIds = [id];
                return;
            }
            const index = this.selectedTableIds.indexOf(id);
            if (index >= 0) {
                this.selectedTableIds.splice(index, 1);
            } else {
                this.selectedTableIds.push(id);
            }
        },

        clearTableSelection: function () {
            this.selectedTableIds = [];
        },

        prepareCombinationTableIds: function (tableIds) {
            return sortTableIdsByTableOrder(tableIds, this.tables);
        },

        findExistingCombinationByTableIds: function (tableIds, excludedId = null) {
            const signature = buildCombinationSignature(tableIds);
            if (!signature) {
                return null;
            }

            return this.combinations.find((combination) => {
                if (excludedId !== null && combination.id === String(excludedId)) {
                    return false;
                }

                return buildCombinationSignature(combination.tableIds) === signature;
            }) || null;
        },

        replaceRoom: function (room) {
            const normalizedRoom = normalizeRoom(room);
            const index = _.findIndex(this.rooms, (candidate) => {
                return candidate.id === normalizedRoom.id;
            });

            if (index >= 0) {
                Vue.set(this.rooms, index, normalizedRoom);
            } else {
                this.rooms.push(normalizedRoom);
            }

            this.rooms = sortRooms(this.rooms);
            return normalizedRoom;
        },

        replaceTable: function (table) {
            const normalizedTable = normalizeTable(table);
            const index = _.findIndex(this.tables, (candidate) => {
                return candidate.id === normalizedTable.id;
            });

            if (index >= 0) {
                Vue.set(this.tables, index, normalizedTable);
            } else {
                this.tables.push(normalizedTable);
            }

            this.tables = sortTables(this.tables);
            return normalizedTable;
        },

        setEditMode: function (value) {
            this.editMode = value;
        },

        addRoom: async function (data) {
            const room = _.assign({
                name: 'Neuer Bereich',
                width: 600,
                height: 400,
                position: this.rooms.length
            }, data || {});

            const createdRoom = await TableReservationApi.createRoom(
                buildRoomInput(room, getCmsId())
            );

            const normalizedRoom = this.replaceRoom(createdRoom);
            this.selectedRoomId = normalizedRoom.id;

            return normalizedRoom;
        },

        updateRoom: async function (id, data) {
            const index = _.findIndex(this.rooms, (room) => {
                return room.id === id;
            });

            if (index < 0) {
                throw new Error('Room not found');
            }

            const previousRoom = _.cloneDeep(this.rooms[index]);
            const nextRoom = normalizeRoom(_.assign({}, previousRoom, data));
            Vue.set(this.rooms, index, nextRoom);

            try {
                const savedRoom = await TableReservationApi.updateRoom(buildRoomInput(nextRoom));
                return this.replaceRoom(savedRoom);
            } catch (error) {
                Vue.set(this.rooms, index, previousRoom);
                throw error;
            }
        },

        deleteRoom: async function (id) {
            await TableReservationApi.deleteRoom(id);

            this.rooms = this.rooms.filter((room) => {
                return room.id !== id;
            });
            this.tables = this.tables.filter((table) => {
                return table.roomId !== id;
            });

            if (this.selectedRoomId === id) {
                this.selectedRoomId = _.get(this.rooms, '0.id', null);
            }

            if (this.selectedTableId && !this.tables.find((table) => { return table.id === this.selectedTableId; })) {
                this.selectedTableId = null;
            }
        },

        addTable: async function (roomId, data) {
            if (!roomId) {
                throw new Error('Select or create a room first');
            }

            const shape = _.get(data, 'shape', 'round') || 'round';
            const seats = _.get(data, 'seats', shape === 'square' ? 4 : 2);
            const tableCount = this.tables.length + 1;
            const table = _.assign({
                roomId: roomId,
                number: String(tableCount),
                name: 'Tisch ' + tableCount,
                shape: shape,
                x: 100,
                y: 100,
                width: shape === 'square' ? 110 : 80,
                height: shape === 'square' ? 90 : 80,
                seats: seats,
                rotation: 0,
                blocked: false,
                seatSides: getDefaultSeatSides(shape, seats)
            }, data || {});

            const createdTable = await TableReservationApi.createTable(
                buildTableInput(table)
            );

            const normalizedTable = this.replaceTable(createdTable);
            this.selectedTableId = normalizedTable.id;
            return normalizedTable;
        },

        updateTable: async function (id, data) {
            const index = _.findIndex(this.tables, (table) => {
                return table.id === id;
            });

            if (index < 0) {
                throw new Error('Table not found');
            }

            const previousTable = _.cloneDeep(this.tables[index]);
            const nextTable = normalizeTable(_.assign({}, previousTable, data));
            Vue.set(this.tables, index, nextTable);

            try {
                const savedTable = await TableReservationApi.updateTable(buildTableInput(nextTable));
                return this.replaceTable(savedTable);
            } catch (error) {
                Vue.set(this.tables, index, previousTable);
                throw error;
            }
        },

        moveTable: function (id, x, y) {
            return this.updateTable(id, { x: x, y: y });
        },

        deleteTable: async function (id) {
            await TableReservationApi.deleteTable(id);

            this.tables = this.tables.filter((table) => {
                return table.id !== id;
            });

            if (this.selectedTableId === id) {
                this.selectedTableId = null;
            }
        },

        replaceFixture: function (fixture) {
            const normalizedFixture = normalizeFixture(fixture);
            const index = _.findIndex(this.fixtures, (candidate) => {
                return candidate.id === normalizedFixture.id;
            });

            if (index >= 0) {
                Vue.set(this.fixtures, index, normalizedFixture);
            } else {
                this.fixtures.push(normalizedFixture);
            }

            this.fixtures = sortFixtures(this.fixtures);
            return normalizedFixture;
        },

        addFixture: async function (roomId, data) {
            if (!roomId) {
                throw new Error('Select or create a room first');
            }

            const type = _.get(data, 'type', 'rectangle') || 'rectangle';
            const defaultSize = getFixtureDefaultSize(type);
            const fixture = _.assign({
                roomId: roomId,
                type: type,
                label: defaultFixtureLabel(type),
                x: 100,
                y: 100,
                width: defaultSize.width,
                height: defaultSize.height,
                rotation: 0,
                position: this.fixtures.length
            }, data || {});

            const createdFixture = await TableReservationApi.createFixture(
                buildFixtureInput(fixture)
            );

            const normalizedFixture = this.replaceFixture(createdFixture);
            this.selectFixture(normalizedFixture.id);
            return normalizedFixture;
        },

        updateFixture: async function (id, data) {
            const index = _.findIndex(this.fixtures, (fixture) => {
                return fixture.id === id;
            });

            if (index < 0) {
                throw new Error('Object not found');
            }

            const previousFixture = _.cloneDeep(this.fixtures[index]);
            const nextFixture = normalizeFixture(_.assign({}, previousFixture, data));
            Vue.set(this.fixtures, index, nextFixture);

            try {
                const savedFixture = await TableReservationApi.updateFixture(buildFixtureInput(nextFixture));
                return this.replaceFixture(savedFixture);
            } catch (error) {
                Vue.set(this.fixtures, index, previousFixture);
                throw error;
            }
        },

        moveFixture: function (id, x, y) {
            return this.updateFixture(id, { x: x, y: y });
        },

        deleteFixture: async function (id) {
            await TableReservationApi.deleteFixture(id);

            this.fixtures = this.fixtures.filter((fixture) => {
                return fixture.id !== id;
            });

            if (this.selectedFixtureId === id) {
                this.selectedFixtureId = null;
            }
        },

        blockTable: function (id) {
            return this.updateTable(id, { blocked: true });
        },

        unblockTable: function (id) {
            return this.updateTable(id, { blocked: false });
        },

        replaceCombination: function (combination) {
            const normalized = normalizeCombination(combination);
            const index = _.findIndex(this.combinations, (candidate) => {
                return candidate.id === normalized.id;
            });

            if (index >= 0) {
                Vue.set(this.combinations, index, normalized);
            } else {
                this.combinations.push(normalized);
            }

            this.combinations = sortCombinations(this.combinations);
            return normalized;
        },

        createCombination: async function (data) {
            const orderedTableIds = this.prepareCombinationTableIds(_.get(data, 'tableIds', []));
            if (orderedTableIds.length < 2) {
                throw new Error('Select at least two different tables');
            }

            if (this.findExistingCombinationByTableIds(orderedTableIds)) {
                throw new Error('This combination already exists');
            }

            const combination = _.assign({
                name: '',
                position: this.combinations.length,
                tableIds: orderedTableIds
            }, data || {}, {
                tableIds: orderedTableIds
            });

            const created = await TableReservationApi.createTableCombination(
                buildCombinationInput(combination, getCmsId())
            );

            return this.replaceCombination(created);
        },

        updateCombination: async function (id, data) {
            const index = _.findIndex(this.combinations, (combination) => {
                return combination.id === String(id);
            });

            if (index < 0) {
                throw new Error('Combination not found');
            }

            const previous = _.cloneDeep(this.combinations[index]);
            const nextData = _.assign({}, previous, data);
            if (Object.prototype.hasOwnProperty.call(data || {}, 'tableIds')) {
                nextData.tableIds = this.prepareCombinationTableIds(_.get(data, 'tableIds', []));

                if (nextData.tableIds.length < 2) {
                    throw new Error('Select at least two different tables');
                }

                if (this.findExistingCombinationByTableIds(nextData.tableIds, id)) {
                    throw new Error('This combination already exists');
                }
            }

            const next = normalizeCombination(nextData);
            Vue.set(this.combinations, index, next);

            try {
                const saved = await TableReservationApi.updateTableCombination(
                    buildCombinationInput(next)
                );
                return this.replaceCombination(saved);
            } catch (error) {
                Vue.set(this.combinations, index, previous);
                throw error;
            }
        },

        deleteCombination: async function (id) {
            await TableReservationApi.deleteTableCombination(id);
            this.combinations = this.combinations.filter((combination) => {
                return combination.id !== String(id);
            });
        },

        reset: function () {
            this.rooms = [];
            this.tables = [];
            this.fixtures = [];
            this.combinations = [];
            this.selectedTableId = null;
            this.selectedTableIds = [];
            this.selectedFixtureId = null;
            this.selectedRoomId = null;
            this.editMode = false;
        }
    }
});
